import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { MdClose } from 'react-icons/md';

const badgeVariants = cva(
  'inline-flex items-center rounded-md border font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-center justify-center',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-white shadow',
        primary: 'border-transparent bg-primary text-white shadow',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        success: 'border-transparent bg-success text-white',
        destructive: 'border-transparent bg-destructive text-white shadow',
        accent: 'border-transparent bg-accent text-white shadow',
        ghost: 'border-transparent bg-white text-black shadow',
        outline: 'text-foreground',
        'outline-primary': 'border-primary text-primary bg-transparent',
        'outline-destructive': '!border-destructive text-destructive bg-white/50',
        'outline-secondary': 'text-secondary-foreground',
        'outline-accent': 'text-accent-foreground',
        'outline-success': 'border-success text-success bg-white/50',
        'outline-ghost': '!border-gray-200 text-gray-500 bg-white/50',
        'outline-warning': 'border-warning text-warning bg-white/50',
        holidays: 'border-accent text-accent bg-white/50',
      },
      size: {
        xs: 'px-1 py-[1px] text-[10px]',
        sm: 'px-1 py-0.5 text-xs',
        md: 'px-2 py-0.5 text-xs',
        lg: 'px-3 py-1.5 text-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  selected?: boolean;
  onDismiss?: () => void;
}

function Badge({ className, variant, size, children, selected, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant, size }), selected && 'opacity-80', props.onClick && 'cursor-pointer', className)} {...props}>
      <span className="flex items-center gap-1">{variant === 'holidays' && !children ? `🌴 Vacances` : children}</span>
      {selected && (
        <span className="ml-2 cursor-pointer">
          <MdClose />
        </span>
      )}
    </span>
  );
}

export { Badge, badgeVariants };
